import { Suspense, lazy, useEffect } from "react";
import { useAnalytics } from "./hooks/useAnalytics";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import { LanguageProvider } from "./contexts/LanguageContext";
import LanguageLayout from "./components/LanguageLayout";

// Lazy load UI components that use heavy libraries (sonner, radix-ui)
const Toaster = lazy(() => import("@/components/ui/toaster").then(m => ({ default: m.Toaster })));
const Sonner = lazy(() => import("@/components/ui/sonner").then(m => ({ default: m.Toaster })));
const TooltipProvider = lazy(() => import("@/components/ui/tooltip").then(m => ({ default: m.TooltipProvider })));
const FloatingContactButton = lazy(() => import("./components/FloatingContactButton"));
const CookieConsent = lazy(() => import("./components/CookieConsent"));

// Lazy load non-critical pages to reduce initial bundle size

const Contact = lazy(() => import("./pages/Contact"));

const Blog = lazy(() => import("./pages/Blog"));
const Services = lazy(() => import("./pages/Services"));
const About = lazy(() => import("./pages/About"));
const Legal = lazy(() => import("./pages/Legal"));
const Apartments = lazy(() => import("./pages/Apartments"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const ServiceGestionLocative = lazy(() => import("./pages/ServiceGestionLocative"));
const ServiceConciergerie = lazy(() => import("./pages/ServiceConciergerie"));
const ServiceYachtCharter = lazy(() => import("./pages/ServiceYachtCharter"));
const ServiceBilletterie = lazy(() => import("./pages/ServiceBilletterie"));
const ServiceAutomobiles = lazy(() => import("./pages/ServiceAutomobiles"));
const ServiceStaff = lazy(() => import("./pages/ServiceStaff"));
const ServiceALaCarte = lazy(() => import("./pages/ServiceALaCarte"));
const ServiceBailMobilite = lazy(() => import("./pages/ServiceBailMobilite"));
const Reviews = lazy(() => import("./pages/Reviews"));
const Merci = lazy(() => import("./pages/Merci"));


// Create a react-query client
const queryClient = new QueryClient();

// Simple loading fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

// Root redirect: detect language and redirect to /:lang/
function RootRedirect() {
  const supported = ['fr', 'en'];
  const savedLang = localStorage.getItem('language');
  if (savedLang && supported.includes(savedLang)) {
    return <Navigate to={`/${savedLang}`} replace />;
  }
  const browserLang = navigator.language.split('-')[0];
  const lang = supported.includes(browserLang) ? browserLang : 'fr';
  return <Navigate to={`/${lang}`} replace />;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

const App = () => {
  useAnalytics();
  return (
  <QueryClientProvider client={queryClient}>
    <Suspense fallback={null}>
      <TooltipProvider>
        <LanguageProvider>
          <BrowserRouter>
            <ScrollToTop />
            <Suspense fallback={null}>
              <Toaster />
              <Sonner />
              <FloatingContactButton />
              <CookieConsent />
            </Suspense>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Root redirect to detected language */}
                <Route path="/" element={<RootRedirect />} />
                
                {/* All routes under /:lang */}
                <Route path="/:lang" element={<LanguageLayout />}>
                  <Route index element={<Index />} />
                  <Route path="services" element={<Services />} />
                  <Route path="services/gestion-locative" element={<ServiceGestionLocative />} />
                  <Route path="services/conciergerie" element={<ServiceConciergerie />} />
                  <Route path="services/yacht-charter" element={<ServiceYachtCharter />} />
                  <Route path="services/billetterie" element={<ServiceBilletterie />} />
                  <Route path="services/automobiles" element={<ServiceAutomobiles />} />
                  <Route path="services/staff" element={<ServiceStaff />} />
                  <Route path="services/prestations-a-la-carte" element={<ServiceALaCarte />} />
                  <Route path="services/bail-mobilite-aix-en-provence" element={<ServiceBailMobilite />} />
                  <Route path="about" element={<About />} />
                  <Route path="avis" element={<Reviews />} />
                  <Route path="reviews" element={<Reviews />} />
                  
                  <Route path="blog" element={<Blog />} />
                  {/* Redirect obsolete blog slugs (deleted articles) to blog index to avoid Soft 404 */}
                  <Route path="blog/gestion-avis-e-reputation-location" element={<Navigate to="../blog" replace />} />
                  <Route path="blog/:slug" element={<BlogPost />} />
                  <Route path="contact" element={<Contact />} />
                  
                  <Route path="legal" element={<Legal />} />
                  <Route path="apartments" element={<Apartments />} />
                  <Route path="merci" element={<Merci />} />
                  <Route path="thank-you" element={<Merci />} />
                  
                </Route>
                
                {/* Legacy routes without lang prefix - redirect */}
                <Route path="/services" element={<LegacyRedirect />} />
                <Route path="/services/gestion-locative" element={<LegacyRedirect />} />
                <Route path="/services/conciergerie" element={<LegacyRedirect />} />
                <Route path="/services/yacht-charter" element={<LegacyRedirect />} />
                <Route path="/services/billetterie" element={<LegacyRedirect />} />
                <Route path="/services/automobiles" element={<LegacyRedirect />} />
                <Route path="/services/staff" element={<LegacyRedirect />} />
                <Route path="/services/prestations-a-la-carte" element={<LegacyRedirect />} />
                <Route path="/services/bail-mobilite-aix-en-provence" element={<LegacyRedirect />} />
                <Route path="/about" element={<LegacyRedirect />} />
                
                <Route path="/blog" element={<LegacyRedirect />} />
                <Route path="/contact" element={<LegacyRedirect />} />
                
                <Route path="/legal" element={<LegacyRedirect />} />
                <Route path="/apartments" element={<LegacyRedirect />} />
                
                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </LanguageProvider>
      </TooltipProvider>
    </Suspense>
  </QueryClientProvider>
  );
};

// Redirect old routes (e.g. /services -> /fr/services)
function LegacyRedirect() {
  const savedLang = localStorage.getItem('language') || 'fr';
  const path = window.location.pathname;
  return <Navigate to={`/${savedLang}${path}`} replace />;
}

export default App;
